import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { ChannelValue } from 'api/captureRate'
import ModalContainer from 'components/ModalContainer'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { currencyFromCents } from 'utilities/formatters'

const t = buildTranslate('capture_rate.revenue_sources.modal')
const tChannel = buildTranslate(
  'capture_rate.revenue_sources.table.channel_value'
)

type Props = {
  isOpen: boolean
  isLoading: boolean
  errorMessage?: string
  rawChannel: string
  fromValue: ChannelValue
  toValue: ChannelValue
  totalCents: number
  orderCount: number
  onKeep: () => void
  onChange: () => void
}

const ConfirmChannelModal = ({
  isOpen,
  isLoading,
  errorMessage,
  rawChannel,
  fromValue,
  toValue,
  totalCents,
  orderCount,
  onKeep,
  onChange,
}: Props) => {
  const [css] = useStyletron()
  const fromLabel = tChannel(fromValue)
  const toLabel = tChannel(toValue)
  const captionClass = css({
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  })

  return (
    <ModalContainer
      isOpen={isOpen}
      isLoading={isLoading}
      onClose={onKeep}
      title={t('title', { rawChannel, to: toLabel })}
      hideCancel
      showBottomProceed={false}
      buttonGroupChildren={
        <>
          <Button
            kind="secondary"
            className="mr-s"
            disabled={isLoading}
            onClick={onKeep}
          >
            {t('cancel')}
          </Button>
          <Button
            kind="primary"
            disabled={isLoading}
            isLoading={isLoading}
            onClick={onChange}
          >
            {t('change_channel')}
          </Button>
        </>
      }
    >
      <Text.BodyText4 tag="div" color="grey70" className="mt-s mb-m">
        {t('description', { rawChannel })}
      </Text.BodyText4>
      <Text.BodyText6
        tag="div"
        bold
        color="grey70"
        className={`mb-xs ${captionClass}`}
      >
        {t('channel_label')}
      </Text.BodyText6>
      <div
        className={`mb-m ${css({
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        })}`}
      >
        <Text.BodyText4
          bold
          color="grey90"
          tag="span"
          className={css({
            backgroundColor: palette.grey10,
            borderRadius: '6px',
            padding: '6px 12px',
          })}
        >
          {fromLabel}
        </Text.BodyText4>
        <Text.Header4 color="grey70" tag="span">
          →
        </Text.Header4>
        <Text.BodyText4
          bold
          color="belizeHole"
          tag="span"
          className={css({
            backgroundColor: palette.belizeHole10,
            borderRadius: '6px',
            padding: '6px 12px',
          })}
        >
          {toLabel}
        </Text.BodyText4>
      </div>
      <div
        className={`p-m mb-m ${css({
          backgroundColor: palette.grey05,
          border: `1px solid ${palette.grey20}`,
          borderRadius: '10px',
        })}`}
      >
        {totalCents > 0 ? (
          <>
            <Text.BodyText6 bold color="grey70" className={captionClass}>
              {t('impact_caption')}
            </Text.BodyText6>
            <Text.Header3 tag="div" color="grey90">
              {currencyFromCents(totalCents)}
            </Text.Header3>
            <Text.BodyText5 color="grey70">
              {t('impact_transactions', { count: orderCount })}
            </Text.BodyText5>
          </>
        ) : (
          <Text.BodyText4 color="grey70">{t('impact_empty')}</Text.BodyText4>
        )}
      </div>
      <div
        className={`p-m ${css({
          backgroundColor: palette.eggYolk10,
          borderRadius: '8px',
          // Offsets ModalContainer's 48px button-row top margin when this box is last.
          marginBottom: errorMessage ? 0 : '-32px',
        })}`}
      >
        <Text.BodyText5 bold color="eggYolk90">
          {t('warning')}
        </Text.BodyText5>
      </div>
      {errorMessage && (
        <div role="alert" className={`mt-xs ${css({ marginBottom: '-32px' })}`}>
          <Text.BodyText5 color="negative" tag="div">
            {errorMessage}
          </Text.BodyText5>
        </div>
      )}
    </ModalContainer>
  )
}

export default ConfirmChannelModal
